/**
 * Classify a CMS-provided link string. Shared by the admin Zod validator
 * and the public renderer so both agree on what "valid" means.
 *
 * - internal: starts with a single "/" (not "//" — that would be
 *   protocol-relative)
 * - external: a well-formed https:// URL
 * - invalid: anything else (empty, javascript:, data:, http:, mailto:,
 *   tel:, protocol-relative, malformed)
 */
export type CmsLink =
  | { kind: "internal"; to: string }
  | { kind: "external"; href: string }
  | { kind: "invalid" };

export function classifyCmsLink(raw: string | null | undefined): CmsLink {
  if (typeof raw !== "string") return { kind: "invalid" };
  const trimmed = raw.trim();
  if (trimmed.length === 0) return { kind: "invalid" };

  // Protocol-relative — reject.
  if (trimmed.startsWith("//")) return { kind: "invalid" };

  // Internal path.
  if (trimmed.startsWith("/")) return { kind: "internal", to: trimmed };

  // External — must parse and be https.
  try {
    const url = new URL(trimmed);
    if (url.protocol !== "https:") return { kind: "invalid" };
    return { kind: "external", href: url.toString() };
  } catch {
    return { kind: "invalid" };
  }
}

export function isValidCmsLink(raw: string | null | undefined): boolean {
  return classifyCmsLink(raw).kind !== "invalid";
}